<template>
    <div class="graph-wrapper">
        <div ref="chartContent" class="chart-content is-flex is-grow-1">
            <svg ref="svgcontent"><defs id="area-gradients"></defs></svg>
        </div>
        <slot name="legend">
            <div class="legends is-flex py-3 mx-3">
                <div
                    class="legend-box is-flex mr-3"
                    v-for="(i, idx) in items"
                    :key="`${idx}-${i.label}`"
                    :class="{ 'disabled-item': checkIfItemIsDisabled(i) }"
                    :style="{ cursor: i.values.length > 0 && filterable ? 'pointer' : 'default' }"
                    @click="filter(i.label)"
                >
                    <div
                        class="color-box mr-1"
                        :style="{
                            'background-color':
                                i.values.length > 0 ? mappedColorArray[idx] : '#818181',
                        }"
                    />
                    <p v-if="useMl">{{ $t(i.label) }}</p>
                    <p v-else>{{ i.label }}</p>
                </div>
            </div>
        </slot>
    </div>
</template>

<script>
import CockpitChart from '@/components/shared/chart/CockpitChart';
import { palette as paleta } from '@/functions/color/Palette';

export default {
    extends: CockpitChart,
    props: {
        /* As linhas do gráfico. Cada item do array será uma linha no gráfico. */
        items: {
            type: Array,
            default: () => [
                /*  Estrutura de exemplo de um item do array:
                        {
                            color: '#FF0000' (opcional),
                            label: 'Objetivo',
                            values: [2, 5, 8, 14, ...],
                            painted: true/false (se deve pintar a área abaixo desta linha em específica),
                            gradient: true/false (se deve pintar com gradiente, tem que ser usado com painted = true),
                            verticalLine: true/false (se mostra uma linha tracejada no valor atual)
                        } */
            ],
        },
        /* Labels do eixo X */
        xAxis: {
            type: Array,
            default: () => [
                /* Array de Strings com as labels do eixo X */
            ],
        },
        /* Se pinta abaixo do gráfico */
        painted: {
            type: Boolean,
            default: true,
        },
        /* Se deve suavizar a curva */
        smoothing: {
            type: Boolean,
            default: true,
        },
        /* Se deve permitir filtrar as linhas do gráfico ou não */
        filterable: {
            type: Boolean,
            default: true,
        },
        /* Se deve mostrar as bolinhas no gráfico em cada ponto */
        dots: {
            type: Boolean,
            default: true,
        },
        /* Função para renderizar o tooltip. Retornar uma string com HTML */
        tooltipRender: {
            type: Function,
            default: (data) => data.items.map((e) => `${e.label}: ${e.value || ''}`).join('<br>'),
        },
        /* Grossura da linha e dos círculos */
        lineThickness: {
            type: Number,
            default: 3,
        },
        /* Valor mínimo para o exio vertical. */
        minVerticalValue: {
            type: Number,
            default: 10,
        },
        /* Se deve usar tradução */
        useMl: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            colors: paleta.getBarColors(),
            dotRadius: 3,
            tooltip: null,
            tooltipId: `linegraph-tooltip-${Math.round(Math.random() * 5000)}`,
            currentTooltipPos: -1,
            mappedColorArray: [],
            filtered: [],
            margin: {
                top: 10,
                right: 20,
                bottom: 20,
                left: 40,
            },
            svg: null,
            xScale: null,
            yScale: null,
            rebuild: null,
        };
    },
    created() {
        /* Redesenhar o gráfico. Remove o SVG e refaz ele. */
        this.rebuild = this.debounce(function () {
            this.$d3.select(this.$refs.svgcontent).selectAll('g').remove();
            this.build();
        }, 550);
        window.addEventListener('resize', this.rebuild);
    },
    mounted() {
        this.build();
    },
    destroyed() {
        window.removeEventListener('resize', this.rebuild);

        if (this.tooltip != null) {
            this.tooltip.remove();
        }
    },
    computed: {
        noData() {
            return this.items.length == 0;
        },
    },
    watch: {
        /* Se os itens ou o eixo mudar, reconstruir o gráfico novamente */
        items: {
            handler: function () {
                this.immediateRebuild();
            },
            deep: true,
        },
        xAxis: {
            handler: function () {
                this.immediateRebuild();
            },
            deep: true,
        },
    },
    methods: {
        build() {
            const width =
                this.$refs.chartContent.offsetWidth - (this.margin.left + this.margin.right);
            const height =
                this.$refs.chartContent.offsetHeight - (this.margin.bottom + this.margin.top);

            this.xScale = this.$d3
                .scaleOrdinal()
                .domain(this.xAxis.map((e, i) => i))
                .range(this.getXRange(this.xAxis, width));

            const filteredItems = this.items.filter(
                (item) => this.filtered.indexOf(item.label) == -1
            );
            const filteredColorArray = filteredItems.map(
                (item, idx) => item.color || this.colors[idx % this.colors.length]
            );
            const color = this.$d3.scaleOrdinal(filteredColorArray);

            const yMax = this.getNextIntegerNumber(
                Math.max(...filteredItems.flatMap((item) => item.values), this.minVerticalValue)
            );

            this.yScale = this.$d3.scaleLinear().domain([0, yMax]).range([height, 0]);

            this.mappedColorArray = this.items.map(
                (item, idx) => item.color || this.colors[idx % this.colors.length]
            );

            const line = this.$d3
                .line()
                .x((d, i) => this.xScale(i))
                .y((d) => this.yScale(d));

            const area = this.$d3
                .area()
                .x((d, i) => this.xScale(i))
                .y0(this.yScale(0))
                .y1((d) => this.yScale(d));

            if (this.smoothing) {
                line.curve(this.$d3.curveMonotoneX);
                area.curve(this.$d3.curveMonotoneX);
            }

            if (this.$d3.select(`#${this.tooltipId}`).empty()) {
                this.tooltip = this.$d3
                    .select('body')
                    .append('div')
                    .attr('class', 'chart-tooltip')
                    .attr('id', this.tooltipId)
                    .style('opacity', 0);
            }

            this.svg = this.$d3
                .select(this.$refs.svgcontent)
                .attr('width', width + this.margin.left + this.margin.right)
                .attr('height', height + this.margin.top + this.margin.bottom)
                .append('g')
                .attr('transform', `translate(${this.margin.left || 0}, ${this.margin.top || 0})`);

            this.svg
                .append('g')
                .attr('class', 'x axis')
                .attr('transform', `translate(0, ${height || 0})`)
                .call(this.$d3.axisBottom(this.xScale).tickValues(this.xAxis));

            this.svg
                .append('g')
                .attr('class', 'y axis')
                .call(
                    this.$d3
                        .axisLeft(this.yScale)
                        .ticks(height / 25)
                        .tickFormat(this.numberFormatter)
                );

            const dots = [];

            this.$d3.select('#area-gradients').selectAll('*').remove();

            filteredItems.forEach((item, idxItem) => {
                const values = item.values.slice(0, this.xAxis.length);

                this.svg
                    .append('path')
                    .datum(values)
                    .attr('class', 'line')
                    .attr('d', line)
                    .style('fill', '#0000')
                    .style('stroke', color(item.label))
                    .style('stroke-width', this.lineThickness);

                if (this.painted === true || item.painted === true) {
                    let fill = '';

                    if (item.gradient === true) {
                        const defs = this.$d3.select('#area-gradients');
                        const gradientId = `grad-${idxItem}`;
                        const gradient = defs
                            .append('linearGradient')
                            .attr('id', gradientId)
                            .attr('x1', '0')
                            .attr('x2', '0')
                            .attr('y1', '0')
                            .attr('y2', '1');

                        gradient
                            .append('stop')
                            .attr('offset', '0%')
                            .attr('stop-color', this.getAlfa(color(item.label), 35))
                            .attr('stop-opacity', '0.5');

                        gradient.append('stop').attr('offset', '100%').attr('stop-opacity', '0');

                        this.svg
                            .append('path')
                            .datum(item.values.slice(0, this.xAxis.length))
                            .attr('class', 'area')
                            .style('fill', `url(#${gradientId})`)
                            .attr('d', area);

                        fill = `url(#${gradientId})`;
                    } else {
                        fill = this.getAlfa(color(item.label), 35);
                    }

                    this.svg
                        .append('path')
                        .datum(item.values.slice(0, this.xAxis.length))
                        .attr('class', 'area')
                        .style('fill', fill)
                        .attr('d', area);
                }

                if (item.values.length > 0 && item.verticalLine === true) {
                    const x = this.xScale(item.values.length - 1);

                    this.svg
                        .append('line')
                        .attr('x1', x)
                        .attr('y1', 0)
                        .attr('x2', x)
                        .attr('y2', height)
                        .attr('stroke-dasharray', '5, 5')
                        .attr('class', 'line')
                        .style('stroke', color(item.label))
                        .style('stroke-width', this.lineThickness)
                        .style('opacity', '0.5');
                }

                values.slice(0, this.xAxis.length).forEach((v, idx) => {
                    dots.push({
                        index: idx,
                        label: item.label,
                        value: v,
                    });
                });
            });

            if (this.dots === true) {
                this.svg
                    .selectAll('.dot')
                    .data(dots)
                    .enter()
                    .append('circle')
                    .attr('class', 'dot')
                    .attr('cx', (dot) => this.xScale(dot.index))
                    .attr('cy', (dot) => this.yScale(dot.value))
                    .attr('r', this.dotRadius)
                    .style('stroke', (dot) => color(dot.label))
                    .style('stroke-width', this.lineThickness)
                    .style('fill', '#FFF');
            }

            if (this.items.some((e) => e.values.length > 0)) {
                const self = this;

                const rect = this.svg.append('rect').attr('class', 'hover-rect');

                rect.attr('width', width)
                    .attr('height', height)
                    .style('fill', 'transparent')
                    .on('mouseover', function () {
                        self.tooltip
                            .transition()
                            .duration(200)
                            .style('opacity', this.legendOpacity)
                            .style('color', '#FFF');
                    })
                    .on('mousemove', function () {
                        const xMousePos = self.$d3.mouse(this)[0] + width / (self.xAxis.length * 2);
                        const xAxisPos = self.$d3.bisect(self.xScale.range(), xMousePos) - 1;

                        if (xAxisPos != self.currentTooltipPos) {
                            self.currentTooltipPos = xAxisPos;

                            const highestYPos = Math.max(
                                ...self.items
                                    .filter((item) => !self.filtered.includes(item.label))
                                    .map((item) => item.values[xAxisPos] || 0)
                            );
                            const svgRect = rect.node().getBoundingClientRect();

                            const fnArgs = {
                                xAxis: self.xAxis[xAxisPos],
                                items: self.items
                                    .map((e, idx) => ({
                                        label: e.label,
                                        color: e.color || self.mappedColorArray[idx],
                                        value: e.values[xAxisPos],
                                    }))
                                    .filter((i) => !self.filtered.includes(i.label)),
                            };

                            self.tooltip
                                .html(self.tooltipRender(fnArgs))
                                .style(
                                    'left',
                                    `${
                                        svgRect.x +
                                        self.xScale(xAxisPos) -
                                        self.tooltip.node().offsetWidth / 2
                                    }px`
                                )
                                .style(
                                    'top',
                                    `${
                                        svgRect.y +
                                        self.yScale(highestYPos) -
                                        self.tooltip.node().offsetHeight -
                                        self.dotRadius * 2 -
                                        8
                                    }px`
                                );

                            const tooltipElement = document.querySelector(`#${self.tooltipId}`);

                            if (
                                svgRect.x +
                                    self.xScale(xAxisPos) -
                                    self.tooltip.node().offsetWidth / 2 <
                                0
                            ) {
                                self.tooltip.style('left', '0px');

                                const spaceFromScreenInPx = 61;
                                const spaceBetweenDots = self.xScale(1) - self.xScale(0);
                                tooltipElement.style.setProperty(
                                    '--arrow-left',
                                    `${spaceFromScreenInPx + spaceBetweenDots * xAxisPos}px`
                                );
                            } else {
                                tooltipElement.style.setProperty('--arrow-left', '50%');
                            }
                        }
                    })
                    .on('mouseout', (d) => {
                        self.tooltip.transition().duration(500).style('opacity', 0);
                        self.currentTooltipPos = -1;
                    });

                rect.raise();
            }
        },
        immediateRebuild() {
            this.$d3.select(this.$refs.svgcontent).selectAll('g').remove();
            this.build();
        },
        /*  Será executado ao clicar em um item da legenda do gráfico.
                redesenha só a parte interna do gráfico e reajusta o eixo Y */
        filter(label) {
            /* Se o gráfico não for filtrável, nada é feito. */
            if (!this.filterable) {
                return;
            }

            let width =
                this.$refs.chartContent.offsetWidth - (this.margin.left + this.margin.right); // Use the window's width
            let height =
                this.$refs.chartContent.offsetHeight - (this.margin.bottom + this.margin.top); // Use the window's height

            let itemFromLabel = this.items.find((i) => i.label == label);

            if (itemFromLabel && itemFromLabel.values.length > 0) {
                /* Se a label não existir no array de removidos... */
                let labelIndex = this.filtered.indexOf(label);

                if (labelIndex == -1) {
                    /* Adicionar a label que foi retirada */
                    this.filtered.push(label);

                    /* Se todos foram fetirados, resetar */
                    if (
                        this.filtered.length == this.items.filter((i) => i.values.length > 0).length
                    ) {
                        this.filtered = [];
                    }
                } else {
                /* Se não, remove ele */
                    this.filtered.splice(labelIndex, 1);
                }

                let filteredItems = this.items.filter(
                    (item) => this.filtered.indexOf(item.label) == -1
                );

                let filteredColorArray = filteredItems.map((item, idx) => {
                    return item.color || this.colors[idx % this.colors.length];
                });

                let color = this.$d3.scaleOrdinal(filteredColorArray);

                this.svg.selectAll('.line').remove();
                this.svg.selectAll('.area').remove();

                let yMax = this.getNextIntegerNumber(
                    Math.max(...filteredItems.flatMap((item) => item.values), this.minVerticalValue)
                );

                let dots = [];

                this.yScale = this.$d3.scaleLinear().domain([0, yMax]).range([height, 0]);

                this.xScale = this.$d3
                    .scaleOrdinal()
                    .domain(this.xAxis)
                    .range(this.getXRange(this.xAxis, width));

                let line = this.$d3
                    .line()
                    .x((d, i) => this.xScale(i))
                    .y((d) => this.yScale(d));

                let area = this.$d3
                    .area()
                    .x((d, i) => this.xScale(i))
                    .y0(this.yScale(0))
                    .y1((d) => this.yScale(d));

                if (this.smoothing) {
                    line.curve(this.$d3.curveMonotoneX);
                    area.curve(this.$d3.curveMonotoneX);
                }

                this.svg
                    .select('.y')
                    .transition()
                    .call(
                        this.$d3
                            .axisLeft(this.yScale)
                            .ticks(height / 25)
                            .tickFormat(this.numberFormatter)
                    )
                    .duration(500);

                filteredItems.forEach((item, idxItem) => {
                    let values = item.values.slice(0, this.xAxis.length);

                    this.svg
                        .append('path')
                        .datum(values)
                        .attr('class', 'line')
                        .attr('d', line)
                        .style('fill', '#0000')
                        .transition()
                        .duration(500)
                        .style('stroke', color(item.label))
                        .style('stroke-width', this.lineThickness);

                    if (this.painted === true || item.painted === true) {
                        let fill = '';

                        /* Parte para gerar o gradiente abaixo da linha */
                        if (item.gradient === true) {
                            let defs = this.$d3.select('#area-gradients');

                            let gradientId = `grad-${idxItem}`;

                            if (!defs.select(`#${gradientId}`).empty()) {
                                defs.select(`#${gradientId}`).remove();
                            }

                            let gradient = defs
                                .append('linearGradient')
                                .attr('id', gradientId)
                                .attr('x1', '0')
                                .attr('x2', '0')
                                .attr('y1', '0')
                                .attr('y2', '1');

                            /* Parte colorida do gradiente */
                            gradient
                                .append('stop')
                                .attr('offset', '0%')
                                .attr('stop-color', this.getAlfa(color(item.label), 35))
                                .attr('stop-opacity', '0.5');

                            /* Parte transparente do gradiente */
                            gradient
                                .append('stop')
                                .attr('offset', '100%')
                                .attr('stop-opacity', '0');

                            this.svg
                                .append('path')
                                .datum(item.values.slice(0, this.xAxis.length))
                                .attr('class', 'area')
                                .style('fill', `url(#${gradientId})`)
                                .attr('d', area);

                            fill = `url(#${gradientId})`;
                        } else {
                            fill = this.getAlfa(color(item.label), 35);
                        }

                        this.svg
                            .append('path')
                            .datum(item.values.slice(0, this.xAxis.length))
                            .attr('class', 'area')
                            .style('fill', fill)
                            .attr('d', area);
                    }

                    if (item.values.length > 0 && item.verticalLine === true) {
                        let x = this.xScale(item.values.length - 1);

                        this.svg
                            .append('line')
                            .attr('x1', x)
                            .attr('y1', 0)
                            .attr('x2', x)
                            .attr('y2', height)
                            .attr('stroke-dasharray', '5, 5')
                            .attr('class', 'line')
                            .style('stroke', color(item.label))
                            .style('stroke-width', this.lineThickness)
                            .style('opacity', '0.5');
                    }

                    item.values.slice(0, this.xAxis.length).forEach((v, idx) => {
                        dots.push({
                            index: idx,
                            label: item.label,
                            value: v,
                        });
                    });
                });

                this.svg.selectAll('.dot').remove();

                if (this.dots === true) {
                    this.svg
                        .selectAll('.dot')
                        .data(dots)
                        .enter()
                        .append('circle')
                        .attr('class', 'dot')
                        .attr('cx', (dot) => this.xScale(dot.index))
                        .attr('cy', (dot) => this.yScale(dot.value))
                        .attr('r', this.dotRadius)
                        .style('stroke', (dot) => color(dot.label))
                        .style('stroke-width', this.lineThickness)
                        .style('fill', '#FFF');
                }

                this.$d3.select('.hover-rect').raise();
            }
        },
        /* Vai definir se o item vai ser clicável ou não */
        checkIfItemIsDisabled(item) {
            if (this.filterable) {
                return this.filtered.includes(item.label);
            } else {
                return item.values.length == 0;
            }
        },
    },
};
</script>

<style lang="scss" scoped>
.graph-wrapper {
    display: flex;
    flex-direction: column;

    .legends {
        font-size: 10px;
        text-transform: uppercase;
        font-weight: bold;
        overflow-x: auto;

        &::before,
        &::after {
            content: ' ';
            margin: auto;
        }

        .legend-box {
            white-space: nowrap;

            .color-box {
                width: 13px;
                height: 13px;
            }

            &.disabled-item .color-box {
                background-color: #818181 !important;
            }
        }
    }
}
</style>
